import { Popover, Tooltip } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useEffect, useMemo, useRef, useState } from 'react';

import {
  type CalendarEntry,
  type CalendarKind,
  type CalendarTerm,
  covers,
  DATE,
  entryWhen,
  isWeekend,
  monthGrid,
  tint,
  WEEKDAYS,
} from './types';

// Bars shown per week row before a day says "+N".
const MAX_LANES = 3;
const DAY_HEAD = 30;
const LANE = 22;

interface Segment {
  entry: CalendarEntry;
  startCol: number;
  endCol: number;
  continuesLeft: boolean;
  continuesRight: boolean;
  lane: number;
}

// Lays one week's entries out in lanes, Google Calendar style: earlier and
// longer entries take the upper lanes; an entry keeps one lane for the week.
function layoutWeek(week: Dayjs[], entries: CalendarEntry[]): Segment[] {
  const weekStart = week[0].format(DATE);
  const weekEnd = week[6].format(DATE);
  const segments = entries
    .filter((e) => e.start_date <= weekEnd && e.end_date >= weekStart)
    .map((entry) => {
      const startCol = Math.max(0, dayjs(entry.start_date).diff(week[0], 'day'));
      const endCol = Math.min(6, dayjs(entry.end_date).diff(week[0], 'day'));
      return {
        entry,
        startCol,
        endCol,
        continuesLeft: entry.start_date < weekStart,
        continuesRight: entry.end_date > weekEnd,
        lane: 0,
      };
    })
    .sort(
      (a, b) =>
        a.startCol - b.startCol ||
        b.endCol - b.startCol - (a.endCol - a.startCol) ||
        (a.entry.start_time ?? '').localeCompare(b.entry.start_time ?? ''),
    );

  const lanes: boolean[][] = [];
  for (const seg of segments) {
    let lane = 0;
    while (lanes[lane]?.slice(seg.startCol, seg.endCol + 1).some(Boolean)) lane++;
    lanes[lane] ??= Array(7).fill(false);
    for (let c = seg.startCol; c <= seg.endCol; c++) lanes[lane][c] = true;
    seg.lane = lane;
  }
  return segments;
}

interface Props {
  month: Dayjs;
  entries: CalendarEntry[];
  kinds: Map<number, CalendarKind>;
  terms: CalendarTerm[];
  canEdit: boolean;
  onCreate: (startDate: string, endDate: string) => void;
  // Left out for viewers: entries only show their details on hover.
  onOpen?: (entry: CalendarEntry) => void;
}

// A month as 6 Monday-first weeks. Drag across days (or click one) to add an
// entry; click a bar to open it. Holidays tint their days; days outside every
// term are shaded.
export default function MonthView({
  month,
  entries,
  kinds,
  terms,
  canEdit,
  onCreate,
  onOpen,
}: Props) {
  const weeks = useMemo(() => monthGrid(month), [month]);
  const today = dayjs().format(DATE);

  // Drag selection: the day pressed and the day the pointer is over.
  const [anchor, setAnchor] = useState<string | null>(null);
  const [hover, setHover] = useState<string | null>(null);
  // The same range for the mouseup listener, which must not wait for a render.
  const drag = useRef<{ anchor: string; hover: string } | null>(null);
  const selection = anchor && hover ? ([anchor, hover].sort() as [string, string]) : null;

  const startDrag = (date: string) => {
    drag.current = { anchor: date, hover: date };
    setAnchor(date);
    setHover(date);
  };
  const extendDrag = (date: string) => {
    if (!drag.current) return;
    drag.current.hover = date;
    setHover(date);
  };

  useEffect(() => {
    const up = () => {
      const range = drag.current;
      if (!range) return;
      drag.current = null;
      setAnchor(null);
      setHover(null);
      const [s, e] = [range.anchor, range.hover].sort();
      onCreate(s, e);
    };
    window.addEventListener('mouseup', up);
    return () => window.removeEventListener('mouseup', up);
  }, [onCreate]);

  const holidayOf = (date: string) =>
    entries.find((e) => covers(e, date) && kinds.get(e.kind_id)?.no_lessons);
  const termStarting = (date: string) => terms.find((t) => t.start_date === date);
  const inTerm = (date: string) => terms.some((t) => covers(t, date));

  return (
    <div className="overflow-hidden border border-gray-200 select-none">
      <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50">
        {WEEKDAYS.map((d, i) => (
          <div
            key={d}
            className={`py-2 text-center text-xs font-semibold tracking-wide ${i >= 5 ? 'text-gray-400' : 'text-gray-500'}`}
          >
            {d}
          </div>
        ))}
      </div>

      {weeks.map((week) => {
        const segments = layoutWeek(week, entries);
        return (
          <div
            key={week[0].format(DATE)}
            className="relative grid grid-cols-7 border-b border-gray-100 last:border-b-0"
            style={{ minHeight: DAY_HEAD + (MAX_LANES + 1) * LANE + 6 }}
          >
            {week.map((day, col) => {
              const date = day.format(DATE);
              const holiday = holidayOf(date);
              const holidayColor = holiday ? kinds.get(holiday.kind_id)?.color : undefined;
              const outside = day.month() !== month.month();
              const selected = selection && selection[0] <= date && date <= selection[1];
              const hidden = segments.filter(
                (s) => s.lane >= MAX_LANES && s.startCol <= col && col <= s.endCol,
              );
              const term = termStarting(date);
              return (
                <div
                  key={date}
                  onMouseEnter={() => extendDrag(date)}
                  className={`relative border-r border-gray-100 last:border-r-0 transition-colors ${
                    canEdit ? 'hover:bg-blue-50/40' : ''
                  } ${!inTerm(date) && !holiday ? 'bg-gray-50/70' : ''} ${selected ? 'bg-blue-100/70!' : ''}`}
                  style={
                    holidayColor && !selected
                      ? { backgroundColor: tint(holidayColor, '14') }
                      : undefined
                  }
                >
                  {/* The whole cell adds an entry; press and drag for several days. */}
                  {canEdit && (
                    <button
                      type="button"
                      aria-label={`${day.format('YYYY.MM.DD')} — нэмэх`}
                      onMouseDown={(e) => {
                        if (e.button === 0) startDrag(date);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onCreate(date, date);
                        }
                      }}
                      className="absolute inset-0 cursor-pointer border-0 bg-transparent p-0 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-blue-500"
                    />
                  )}
                  <div
                    className="pointer-events-none relative flex items-center justify-between px-1.5 pt-1"
                    style={{ height: DAY_HEAD }}
                  >
                    {term ? (
                      <span
                        className="truncate bg-indigo-50 px-1 text-[10px] font-medium text-indigo-500"
                        title={`${term.name} эхэлнэ`}
                      >
                        {term.name}
                      </span>
                    ) : (
                      <span />
                    )}
                    <span
                      className={`flex size-6 shrink-0 items-center justify-center text-xs font-medium ${
                        date === today
                          ? 'bg-blue-600 text-white'
                          : outside
                            ? 'text-gray-300'
                            : holiday
                              ? 'font-semibold'
                              : isWeekend(day)
                                ? 'text-gray-400'
                                : 'text-gray-700'
                      }`}
                      style={
                        holiday && date !== today && !outside ? { color: holidayColor } : undefined
                      }
                    >
                      {day.date() === 1 ? day.format('M/D') : day.date()}
                    </span>
                  </div>

                  {hidden.length > 0 && (
                    <Popover
                      trigger="click"
                      title={day.format('YYYY.MM.DD')}
                      content={
                        <div className="flex w-60 flex-col gap-1">
                          {segments
                            .filter((s) => s.startCol <= col && col <= s.endCol)
                            .map((s) => (
                              <EntryChip
                                key={s.entry.id}
                                entry={s.entry}
                                kind={kinds.get(s.entry.kind_id)}
                                onOpen={onOpen}
                              />
                            ))}
                        </div>
                      }
                    >
                      <button
                        type="button"
                        onMouseDown={(e) => e.stopPropagation()}
                        className="absolute left-1 z-10 cursor-pointer border-0 bg-transparent px-1.5 text-[11px] font-medium text-gray-500 hover:bg-gray-200"
                        style={{ top: DAY_HEAD + MAX_LANES * LANE + 1 }}
                      >
                        +{hidden.length} бусад
                      </button>
                    </Popover>
                  )}
                </div>
              );
            })}

            {/* Bars above the day cells; only the bars catch the pointer. */}
            <div className="pointer-events-none absolute inset-0">
              {segments
                .filter((s) => s.lane < MAX_LANES)
                .map((s) => (
                  <div
                    key={s.entry.id}
                    className="pointer-events-auto absolute px-0.5"
                    style={{
                      top: DAY_HEAD + s.lane * LANE,
                      left: `${(s.startCol / 7) * 100}%`,
                      width: `${((s.endCol - s.startCol + 1) / 7) * 100}%`,
                      height: LANE - 2,
                    }}
                  >
                    <EntryBar segment={s} kind={kinds.get(s.entry.kind_id)} onOpen={onOpen} />
                  </div>
                ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// What a viewer sees on hover: the entry's title, kind, dates and description.
function EntryDetails({ entry, kind }: { entry: CalendarEntry; kind: CalendarKind | undefined }) {
  return (
    <div className="max-w-64 text-xs">
      <div className="text-sm font-semibold">{entry.title}</div>
      <div className="mt-0.5 opacity-80">{entryWhen(entry)}</div>
      {kind && (
        <div className="mt-1 flex items-center gap-1.5">
          <span className="size-2 shrink-0" style={{ backgroundColor: kind.color }} />
          {kind.name}
          {kind.no_lessons && <span className="opacity-70">· хичээлгүй</span>}
        </div>
      )}
      {entry.description && (
        <div className="mt-1 whitespace-pre-line opacity-80">{entry.description}</div>
      )}
    </div>
  );
}

// An entry across its days: all-day entries as solid bars, timed one-day
// entries as a dot with the time. Editors open it; viewers see its details
// on hover.
function EntryBar({
  segment,
  kind,
  onOpen,
}: {
  segment: Segment;
  kind: CalendarKind | undefined;
  onOpen?: (entry: CalendarEntry) => void;
}) {
  const { entry } = segment;
  const color = kind?.color ?? '#9ca3af';
  const timedDot = entry.start_time && entry.start_date === entry.end_date;

  return (
    <Tooltip title={<EntryDetails entry={entry} kind={kind} />} mouseEnterDelay={0.3}>
      <button
        type="button"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={onOpen ? () => onOpen(entry) : undefined}
        className={`flex h-full w-full items-center gap-1 truncate border-0 px-1.5 text-left text-xs leading-none transition ${
          onOpen ? 'cursor-pointer hover:brightness-95' : 'cursor-default'
        } ${timedDot ? 'bg-transparent text-gray-700 hover:bg-gray-100' : 'font-medium text-white'}`}
        style={timedDot ? undefined : { backgroundColor: color }}
      >
        {timedDot && <span className="size-2 shrink-0" style={{ backgroundColor: color }} />}
        {entry.start_time && (
          <span className={timedDot ? 'text-gray-500' : 'opacity-80'}>{entry.start_time}</span>
        )}
        <span className="truncate">{entry.title}</span>
      </button>
    </Tooltip>
  );
}

export function EntryChip({
  entry,
  kind,
  onOpen,
}: {
  entry: CalendarEntry;
  kind: CalendarKind | undefined;
  onOpen?: (entry: CalendarEntry) => void;
}) {
  const content = (
    <>
      <span className="size-2.5 shrink-0" style={{ backgroundColor: kind?.color ?? '#9ca3af' }} />
      {entry.start_time && <span className="text-gray-500">{entry.start_time}</span>}
      <span className="truncate">{entry.title}</span>
    </>
  );
  return onOpen ? (
    <button
      type="button"
      onClick={() => onOpen(entry)}
      className="flex w-full cursor-pointer items-center gap-2 truncate border-0 bg-transparent px-2 py-1 text-left text-sm hover:bg-gray-100"
    >
      {content}
    </button>
  ) : (
    <Tooltip title={<EntryDetails entry={entry} kind={kind} />} placement="right">
      <div className="flex w-full items-center gap-2 truncate px-2 py-1 text-sm">{content}</div>
    </Tooltip>
  );
}
